/**
 * handleCreditsError.js — shared insufficient-credits handler.
 *
 * Two error shapes are recognised:
 *
 *   1. HTTP 402 (legacy ledger, RC 1.33.C-)         → toast with
 *      "View plan" CTA. Body shape: {required, balance}.
 *
 *   2. HTTP 429 USAGE_EXHAUSTED (RC 1.33.B+, RC 1.41.G4.F) →
 *      either opens a `<UsageExhaustedModal>` via the supplied
 *      `onExhausted` callback, or falls back to a clean toast
 *      with "View plan" CTA when no callback is supplied.
 *      Body shape: {code: "USAGE_EXHAUSTED", required_usage,
 *      included_usage_remaining, extra_usage_remaining,
 *      plan_code, action_type, message}.
 *
 * Returns `true` when the error was recognised (caller should
 * suppress its own fallback toast) or `false` otherwise.
 */

import { toast } from 'sonner';

/**
 * @param err                      Error thrown by `apiFetch`.
 * @param opts.refreshCredits      Called after showing the toast so
 *                                 any local balance display refreshes.
 *                                 Optional — safe to omit.
 * @param opts.action              (optional) human label, e.g. "Analyse Fit".
 * @param opts.onExhausted         (optional) callback invoked with the
 *                                 USAGE_EXHAUSTED detail body when a
 *                                 429 fires. When supplied, the modal
 *                                 path is taken; the toast is
 *                                 suppressed. Use this from the
 *                                 wizard / job-detail surfaces that
 *                                 already mount `<UsageExhaustedModal>`.
 * @returns {boolean}              true if handled.
 */
export function handleCreditsError(err, { refreshCredits, action, onExhausted } = {}) {
  if (!err) return false;

  // ── 429 USAGE_EXHAUSTED (RC 1.33.B+) ─────────────────────────────
  if (err.status === 429) {
    const detail = (err.data && err.data.detail) || {};
    if (detail.code === 'USAGE_EXHAUSTED') {
      if (typeof onExhausted === 'function') {
        try { onExhausted(detail); } catch { /* noop */ }
      } else {
        const required = Number(detail.required_usage) || null;
        const available = Math.max(
          0,
          (Number(detail.included_usage_remaining) || 0)
          + (Number(detail.extra_usage_remaining) || 0),
        );
        const headline = action
          ? `Not enough Credits for ${action}`
          : 'Not enough Credits';
        const description = required
          ? `${available} available · ${required} needed. Open Plan to add Credits.`
          : `${available} available. Open Plan to add Credits.`;
        toast.error(headline, {
          description,
          action: {
            label: 'View plan',
            onClick: () => {
              try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
            },
          },
        });
      }
      if (typeof refreshCredits === 'function') {
        try { refreshCredits(); } catch { /* noop */ }
      }
      return true;
    }
  }

  // ── 402 legacy ledger (RC 1.33.C-) ───────────────────────────────
  if (err.status !== 402) return false;
  const data = err.data || {};
  const required = Number(data.required) || null;
  const balance = Number(data.balance) || 0;

  const headline = action
    ? `Not enough Credits for ${action}`
    : 'Not enough Credits';

  const description = required
    ? `${balance} available · ${required} needed. Open Plan for details.`
    : `${balance} available. Open Plan for details.`;

  toast.error(headline, {
    description,
    action: {
      label: 'View plan',
      onClick: () => {
        try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
      },
    },
  });

  if (typeof refreshCredits === 'function') {
    try { refreshCredits(); } catch { /* noop */ }
  }
  return true;
}
